import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class IndexedDBService {

    private dbName = "QUOTE_ASSIST";
    private objectStore = "QUOTATION";
    private rfqIntakeStore = "RFQINTAKE";
    private quoteAssistV2Store = "QUOTE_ASSIST_V2"; // V2 store - now using rfqId as keyPath like V1
    private version = 9; // Increment version to remove sessionIdIndex completely
    private dbReady!: Promise<IDBDatabase>;

    constructor() {
        this.dbReady = this.openDatabase();
    }

    private openDatabase(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                
                // Create QUOTATION store if it doesn't exist
                if (!db.objectStoreNames.contains(this.objectStore)) {
                    const store = db.createObjectStore(this.objectStore, { keyPath: 'rfqId', autoIncrement: false });
                    store.createIndex("idParentIdIndex", ["id", "parentId"], { unique: true });
                }

                // Create RFQINTAKE store if it doesn't exist
                if (!db.objectStoreNames.contains(this.rfqIntakeStore)) {
                    const rfqStore = db.createObjectStore(this.rfqIntakeStore, { keyPath: 'rfqId', autoIncrement: false });
                    rfqStore.createIndex("rfqIdIndex", "rfqId", { unique: true });
                }

                // Handle QUOTE_ASSIST_V2 store - ensure it matches V1 structure exactly
                if (!db.objectStoreNames.contains(this.quoteAssistV2Store)) {
                    // Create new store with rfqId as keyPath (like V1)
                    const v2Store = db.createObjectStore(this.quoteAssistV2Store, { keyPath: 'rfqId', autoIncrement: false });
                    v2Store.createIndex("rfqIdIndex", "rfqId", { unique: true });
                } else {
                    // Store exists, ensure rfqIdIndex exists with correct structure
                    const v2Store = db.transaction(this.quoteAssistV2Store, 'readwrite').objectStore(this.quoteAssistV2Store);
                    
                    // Ensure rfqIdIndex exists with correct structure
                    if (!v2Store.indexNames.contains('rfqIdIndex')) {
                        v2Store.createIndex("rfqIdIndex", "rfqId", { unique: true });
                    }
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    private ensureDBReady(): Promise<IDBDatabase> {
        return this.dbReady;
    }

    // Generic method to get data from any store
    getDataById(id: any): Observable<any> {
        return new Observable(observer => {
            this.ensureDBReady().then(db => {
                const transaction = db.transaction(this.objectStore, "readonly");
                const store = transaction.objectStore(this.objectStore);
                const request = store.get(id);
                request.onsuccess = () => {
                    observer.next(request.result);
                    observer.complete();
                };
                request.onerror = () => observer.error(request.error);
            });
        });
    }

    // Generic method to add/update data in any store
    addData(data: any): Observable<number> {
        return new Observable(observer => {
            this.ensureDBReady().then(db => {
                // Step 1: Delete the existing record (if it exists)
                const deleteTransaction = db.transaction(this.objectStore, "readwrite");
                const deleteStore = deleteTransaction.objectStore(this.objectStore);
                const deleteRequest = deleteStore.delete(data.rfqId);

                deleteRequest.onsuccess = () => {
                    // Step 2: Add or update the new record
                    const addTransaction = db.transaction(this.objectStore, "readwrite");
                    const addStore = addTransaction.objectStore(this.objectStore);
                    const addRequest = addStore.put(data);

                    addRequest.onsuccess = () => {
                        observer.next(data.rfqId);
                        observer.complete();
                    };

                    addRequest.onerror = () => observer.error(addRequest.error);
                };

                deleteRequest.onerror = () => observer.error(deleteRequest.error);
            });
        });
    }

    // Convenience methods for RFQINTAKE store
    getRfqIntakeData(rfqId: string): Observable<any> {
        return new Observable(observer => {
            this.ensureDBReady().then(db => {
                const transaction = db.transaction(this.rfqIntakeStore, "readonly");
                const store = transaction.objectStore(this.rfqIntakeStore);
                const request = store.get(rfqId);
                request.onsuccess = () => {
                    observer.next(request.result);
                    observer.complete();
                };
                request.onerror = () => observer.error(request.error);
            });
        });
    }

    saveRfqIntakeData(data: any): Observable<number> {
        return new Observable(observer => {
            this.ensureDBReady().then(db => {
                // Step 1: Delete the existing record (if it exists)
                const deleteTransaction = db.transaction(this.rfqIntakeStore, "readwrite");
                const deleteStore = deleteTransaction.objectStore(this.rfqIntakeStore);
                const deleteRequest = deleteStore.delete(data.rfqId);

                deleteRequest.onsuccess = () => {
                    // Step 2: Add or update the new record
                    const addTransaction = db.transaction(this.rfqIntakeStore, "readwrite");
                    const addStore = addTransaction.objectStore(this.rfqIntakeStore);
                    const addRequest = addStore.put(data);

                    addRequest.onsuccess = () => {
                        observer.next(data.rfqId);
                        observer.complete();
                    };

                    addRequest.onerror = () => observer.error(addRequest.error);
                };

                deleteRequest.onerror = () => observer.error(deleteRequest.error);
            });
        });
    }

    getAllRfqIntakeData(): Observable<any[]> {
        return new Observable(observer => {
            this.ensureDBReady().then(db => {
                const transaction = db.transaction(this.rfqIntakeStore, "readonly");
                const store = transaction.objectStore(this.rfqIntakeStore);
                const request = store.getAll();

                request.onsuccess = () => {
                    observer.next(request.result);
                    observer.complete();
                };
                request.onerror = () => observer.error(request.error);
            });
        });
    }

    clearRfqIntakeData(): Observable<void> {
        return new Observable<void>(observer => {
            this.ensureDBReady().then(db => {
                const transaction = db.transaction([this.rfqIntakeStore], 'readwrite');
                const objectStore = transaction.objectStore(this.rfqIntakeStore);
                const clearRequest = objectStore.clear();

                clearRequest.onsuccess = () => {
                    observer.next();
                    observer.complete();
                };

                clearRequest.onerror = (event: Event) => {
                    observer.error(event);
                };
            });
        });
    }

   
}